import { flushSync } from "react-dom";

// A change of theme fades from the old colors into the new ones instead of every surface snapping at once.
// The browser's View Transitions API does the work: it takes a picture of the page, lets the change happen,
// then crossfades the picture into the live page (the timing is in src/theme/jungleThemeColors.css, "the
// change of theme"). Only a change made while someone is looking fades: the dark mode switch in the menu,
// the Settings row, the device turning dark at sunset. The first paint never does.
//
// Browsers without the API (Firefox before 144, Safari before 18) and people who ask their device for less
// motion get the change at once, as before.

// On <html> while a theme change is fading, so the fade's timing never applies to any other transition
export const COLOR_THEME_CROSSFADE_ATTRIBUTE = "data-jungle-theme-crossfade";
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

type ViewTransitionLike = {
  ready: Promise<void>;
  updateCallbackDone: Promise<void>;
  finished: Promise<void>;
};
type DocumentWithViewTransitions = Document & {
  startViewTransition?: (updateTheDocument: () => void) => ViewTransitionLike;
};

function doesPersonPreferReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches;
}

/** Runs `applyColorThemeChange` (a store update that changes the theme) inside a crossfade when it can. */
export function changeColorThemeWithCrossfade(applyColorThemeChange: () => void): void {
  const documentWithViewTransitions = document as DocumentWithViewTransitions;
  // A fade needs a visible page, browser support, and permission to use motion.
  const shouldSkipThemeCrossfade = document.visibilityState !== "visible" ||
    typeof documentWithViewTransitions.startViewTransition !== "function" || doesPersonPreferReducedMotion();
  if (shouldSkipThemeCrossfade) {
    applyColorThemeChange();
    return;
  }

  const rootElement = document.documentElement;
  rootElement.setAttribute(COLOR_THEME_CROSSFADE_ATTRIBUTE, "");
  // The browser takes the "after" picture as soon as this callback returns. flushSync makes React render
  // the new theme (JungleColorThemeSync writes it to <html>, the trees swap to their night pictures) inside
  // the callback; without it the fade would run between two pictures of the old page and the new colors
  // would snap in after it.
  const viewTransition = documentWithViewTransitions.startViewTransition(() => flushSync(applyColorThemeChange));
  // A page can become hidden after the visibility check, or another transition can skip this fade.
  // `finished` still settles, but `ready` rejects separately. Only ignore a browser cancellation
  // once the theme update succeeded; an exception from the update must still reach error reporting.
  void viewTransition.ready.catch(async error => {
    await viewTransition.updateCallbackDone;
    const isExpectedBrowserCancellation = error instanceof DOMException &&
      (error.name === "InvalidStateError" || error.name === "AbortError");
    if (isExpectedBrowserCancellation) return;
    throw error;
  });
  const stopMarkingTheFade = () => rootElement.removeAttribute(COLOR_THEME_CROSSFADE_ATTRIBUTE);
  // `finished` also settles when the browser skips the fade (a hidden tab, another fade starting)
  viewTransition.finished.then(stopMarkingTheFade, stopMarkingTheFade);
}
